import {
    Box,
    Card,
    Stack,
    styled,
    Table,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import columnShape from 'page-sections/judge/abstract-scored/columnShape';
import HeadingArea from 'page-sections/judge/abstract-scored/heading-area';
import SearchArea from 'page-sections/judge/abstract-scored/search-area';
import { useEffect, useMemo, useState } from 'react';
import {
    useGlobalFilter,
    useAsyncDebounce,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
import { useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';

const HeadTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&:first-of-type': {
        paddingLeft: 24,
    },
    '&:last-of-type': {
        paddingRight: 24,
    },
}));

const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const AbstractScored = () => {
    const [dataAbstract, setDataAbstract] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const theme = useTheme();
    const columns = useMemo(() => columnShape, []);
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state,
        setGlobalFilter,
    } = useTable(
        { columns, data: dataAbstract },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
    ); // handle change pagination
    const navigate = useNavigate();
    const { user } = useAuth();

    const handleScoring = async (data) => {
        navigate(`/dashboard/judge/abstract-score-edit/${data.id}`);
    };

    const [searchValue, setSearchValue] = useState(state.globalFilter);

    const handleSearch = useAsyncDebounce(
        (value) => setGlobalFilter(value || undefined),
        200,
    );

    const getData = async (pageParam = pageNo, limitParam = pageSize) => {
        await axiosInstance
            .get(`/api/prv/judge-submissions`, {
                params: {
                    scored_by_judge_id: user.id,
                    page: pageParam,
                    limit: limitParam,
                },
            })
            .then((res) => {
                const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
                const meta = Array.isArray(res.data) ? null : res.data?.meta || null;
                setDataAbstract(items);
                if (meta) {
                    setTotalPages(meta.totalPages || 0);
                } else {
                    setTotalPages(1);
                }
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        if (!user) return;

        getData(1, pageSize);
        // eslint-disable-next-line
    }, [user]);

    const handleChange = async (_, currentPageNo) => {
        setPageNo(currentPageNo);
        await getData(currentPageNo, pageSize);
    };

    const isSearching = Boolean(searchValue && String(searchValue).trim());

    return (
        <Box pt={2} pb={4}>
            <Card
                sx={{
                    py: 2,
                }}
            >
                <Box px={3}>
                    <HeadingArea />
                </Box>
                <Box px={3}>
                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                    />
                </Box>

                <Scrollbar autoHide={true}>
                    {page.length > 0 ? (
                        <Table
                            {...getTableProps()}
                            sx={{
                                minWidth: 900,
                            }}
                        >
                            <TableHead
                                sx={{
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {headerGroups.map((headerGroup, index) => (
                                    <TableRow
                                        key={index}
                                        {...headerGroup.getHeaderGroupProps()}
                                    >
                                        {headerGroup.headers.map(
                                            (column, index) => (
                                                <HeadTableCell
                                                    key={index}
                                                    {...column.getHeaderProps(
                                                        column.getSortByToggleProps(),
                                                    )}
                                                    sx={{
                                                        paddingY: 0,
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        borderBottom: 0,
                                                        width: column.width,
                                                        color: 'text.secondary',
                                                        minWidth: column.minWidth,
                                                        maxWidth: column.maxWidth,
                                                        '&:last-child': {
                                                            textAlign: 'center',
                                                        },
                                                    }}
                                                >
                                                    {column.render('Header')}
                                                </HeadTableCell>
                                            ),
                                        )}
                                    </TableRow>
                                ))}
                            </TableHead>

                            <TableBody {...getTableBodyProps()}>
                                {page.map((row, index) => {
                                    prepareRow(row);
                                    return (
                                        <TableRow
                                            {...row.getRowProps()}
                                            key={index}
                                        >
                                            {row.cells.map((cell, index) => {
                                                return (
                                                    <BodyTableCell
                                                        key={index}
                                                        {...cell.getCellProps()}
                                                    >
                                                        {cell.render('Cell', {
                                                            handleScoring,
                                                        })}
                                                    </BodyTableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <Box px={3} py={8} textAlign="center">
                            <Typography variant="h6">No data</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {isSearching
                                    ? 'No scored abstracts match your search'
                                    : "You haven't scored any abstracts yet"}
                            </Typography>
                        </Box>
                    )}
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        page={pageNo}
                        count={totalPages}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default AbstractScored;
